import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarOutlined,
  CloudDownloadOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FolderAddOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Typography, theme } from 'antd';
import { goTo } from './howToLinks';

const { Text } = Typography;

// Icon of each step, in the order of the steps in the translations.
const ICONS = {
  pack: {
    once: [SettingOutlined, GlobalOutlined],
    monthly: [CalendarOutlined, EyeOutlined, SendOutlined, EditOutlined],
  },
  fccs: {
    once: [UploadOutlined, SaveOutlined],
    monthly: [CalendarOutlined, WarningOutlined, CloudDownloadOutlined],
  },
  cashFlow: {
    once: [SettingOutlined, UploadOutlined, FolderAddOutlined],
    monthly: [CalendarOutlined, EyeOutlined, CloudDownloadOutlined],
  },
  investments: {
    once: [UploadOutlined, SettingOutlined, FolderAddOutlined],
    monthly: [CalendarOutlined, WarningOutlined, CloudDownloadOutlined],
  },
  parentPl: {
    once: [UploadOutlined, SettingOutlined, FolderAddOutlined],
    monthly: [CalendarOutlined, WarningOutlined, EditOutlined, CloudDownloadOutlined],
  },
};

// Where each step's title leads: the tab, the collapsed section to open and
// the element to scroll to (ids set in the panels). No entry = plain title.
const packSettings = { tab: 'pack', section: 'settings', id: 'howto-pack-settings' };
const LINKS = {
  pack: {
    once: [packSettings, packSettings],
    monthly: [
      { tab: 'pack', id: 'howto-pack-toolbar' },
      { tab: 'pack', id: 'howto-pack-toolbar' },
      { tab: 'pack', section: 'releases', id: 'howto-pack-releases' },
      { tab: 'pack', section: 'releases', id: 'howto-pack-releases' },
    ],
  },
  fccs: {
    once: [
      { tab: 'fccs', section: 'codes', id: 'howto-fccs-codes' },
      { tab: 'fccs', section: 'codes', id: 'howto-fccs-codes' },
    ],
    monthly: [{ tab: 'fccs', id: 'howto-fccs-toolbar' }, null, { tab: 'fccs', id: 'howto-fccs-toolbar' }],
  },
  cashFlow: {
    once: [
      { tab: 'cashFlow', section: 'settings', id: 'howto-cashFlow-settings' },
      { tab: 'cashFlow', id: 'howto-cashFlow-toolbar' },
      packSettings,
    ],
    monthly: [{ tab: 'cashFlow', id: 'howto-cashFlow-toolbar' }, null, { tab: 'cashFlow', id: 'howto-cashFlow-toolbar' }],
  },
  investments: {
    once: [
      { tab: 'investments', id: 'howto-investments-toolbar' },
      { tab: 'investments', section: 'settings', id: 'howto-investments-settings' },
      packSettings,
    ],
    monthly: [
      { tab: 'investments', id: 'howto-investments-toolbar' },
      null,
      { tab: 'investments', id: 'howto-investments-toolbar' },
    ],
  },
  parentPl: {
    once: [
      { tab: 'parentPl', id: 'howto-parentPl-toolbar' },
      { tab: 'parentPl', section: 'settings', id: 'howto-parentPl-settings' },
      packSettings,
    ],
    monthly: [
      { tab: 'parentPl', id: 'howto-parentPl-toolbar' },
      null,
      { tab: 'parentPl', id: 'howto-parentPl-manual' },
      { tab: 'parentPl', id: 'howto-parentPl-toolbar' },
    ],
  },
};

/**
 * "How to use" box at the top of a client-reports tab: what is done once for
 * a company and what every month. Open while the tab is not set up for the
 * company yet; the accountant can open and close it at any time.
 *
 * @param {Object} props - Component props
 * @param {string} props.guide - pack | fccs | cashFlow | investments | parentPl
 * @param {boolean} [props.open] - Whether it starts open; undefined while loading
 * @returns {JSX.Element} The box
 */
export default function HowToCard({ guide, open }) {
  const { t, i18n } = useTranslation('clientReports');
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);
  const [touched, setTouched] = useState(false);

  // Follows the tab's state until the accountant opens or closes it by hand.
  useEffect(() => {
    if (!touched && open !== undefined) setExpanded(open);
  }, [open, touched]);

  const toggle = () => {
    setTouched(true);
    setExpanded((e) => !e);
  };

  const section = (part) => {
    const steps = t(`howTo.${guide}.${part}`, { returnObjects: true });
    if (!Array.isArray(steps)) return null;
    return (
      <Col xs={24} lg={12}>
        <Text strong style={{ display: 'block', marginBottom: 10, color: token.colorPrimary }}>
          {t(`howTo.${part}`)}
        </Text>
        {steps.map((step, i) => {
          const Icon = ICONS[guide][part][i] || QuestionCircleOutlined;
          const link = LINKS[guide]?.[part]?.[i];
          return (
            <div key={step.title} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div
                style={{
                  flex: '0 0 40px',
                  height: 40,
                  borderRadius: 10,
                  background: token.colorBgContainer,
                  border: `1px solid ${token.colorPrimaryBorder}`,
                  color: token.colorPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  position: 'relative',
                }}
              >
                <Icon />
                <span
                  style={{
                    position: 'absolute',
                    top: -7,
                    left: -7,
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    background: token.colorPrimary,
                    color: token.colorWhite,
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: '18px',
                    textAlign: 'center',
                  }}
                >
                  {i + 1}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                {link ? (
                  <Typography.Link strong onClick={() => goTo(link, token.colorPrimary, guide)}>
                    {step.title}
                  </Typography.Link>
                ) : (
                  <Text strong>{step.title}</Text>
                )}
                <div style={{ fontSize: 13, color: token.colorTextSecondary }}>{step.text}</div>
              </div>
            </div>
          );
        })}
      </Col>
    );
  };

  // Only some tabs have a closing note.
  const noteKey = `howTo.${guide}.note`;
  const note = i18n.exists(noteKey, { ns: 'clientReports' }) ? t(noteKey) : '';

  return (
    <div
      style={{
        border: `1px solid ${token.colorPrimaryBorder}`,
        borderLeft: `4px solid ${token.colorPrimary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorPrimaryBg,
        padding: expanded ? '12px 16px 4px' : '8px 16px',
        marginBottom: 16,
      }}
    >
      <Button
        type="text"
        onClick={toggle}
        aria-expanded={expanded}
        style={{ padding: 0, height: 'auto', fontWeight: 600, color: token.colorPrimary }}
        icon={<QuestionCircleOutlined />}
      >
        {t(`howTo.${guide}.title`)}
        <DownOutlined
          style={{
            fontSize: 11,
            marginInlineStart: 6,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Button>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Row gutter={[32, 0]}>
            {section('once')}
            {section('monthly')}
          </Row>
          {note && (
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 8 }}>
              {note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
